import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useModal } from '_hooks'

import {
  Button,
  BUTTON_THEME,
  BUTTON_SIZE,
  Icon,
  IconButton,
  Input,
  INPUT_THEME,
  Pagination,
  usePagination,
} from '@st-operations/uikit/src/v2'
import Papa from 'papaparse'

import { TYPE, TOKEN_STATUS, MAX_PAGE_SIZE, MIN_BASE_ITEMS, COLUMNS_COUNT } from './constants'
import UploadCSVModal from '_components/upload-csv-modal'
import downloadCSV from '_lib/downloadCSV'

import './styles.scss'

// Helpers
const Locale = new Intl.Collator('en-US', { numeric: true })

const filterTokens = (list, value) =>
  list.filter((token) => token.toLowerCase().includes(value.toLowerCase()))

const addToList = (value) => (list) => [...list, value]
const removeFromList = (value) => (list) => list.filter((token) => token !== value)

// Matches a dotted IPv4 address with each octet in the 0-255 range, e.g. 85.17.187.83.
const isValidIPv4 = (value) =>
  /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/.test(value)

const isValidDomain = (value) => {
  if (value === '***') return true
  // Bare IPv4 addresses are valid targets too.
  if (isValidIPv4(value)) return true
  // Each label: starts/ends with alphanumeric, may contain hyphens in the middle, max 63 chars.
  // Must have at least one dot separating labels, with a TLD of 2+ alpha characters.
  // Implicitly rejects protocols, spaces, and any other special characters.
  return /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/.test(value)
}

const Token = ({ value, type, onClick, isInvalid }) => (
  <li
    className={classNames('cr-tokens__item', `cr-tokens__item--${TOKEN_STATUS[type].class}`, {
      'cr-tokens__item--invalid': isInvalid,
    })}
  >
    {value && (
      <>
        <IconButton
          onClick={() => onClick({ value, type })}
          iconProps={{ icon: TOKEN_STATUS[type].icon, type: 'fill' }}
          className="cr-tokens__button-list"
        />
        <span>{value}</span>
      </>
    )}
  </li>
)

// Component
const Tokens = ({ tokens, tokensRef, setHasChanges, tokenType }) => {
  const [newTokensInput, setNewTokensInput] = useState('')
  const [search, setSearch] = useState('')

  const [addedTokens, setAddedTokens] = useState([])
  const [removedTokens, setRemovedTokens] = useState([])
  const [baseTokens, setBaseTokens] = useState([])
  const [invalidDomains, setInvalidDomains] = useState(new Set())
  const uploadCSVModal = useModal(false)
  const invalidRef = useRef(null)

  const isDomainsMode = tokenType === 'domains'

  const addedFiltered = useMemo(() => addedTokens.sort(Locale.compare), [addedTokens])
  const removedFiltered = useMemo(() => removedTokens.sort(Locale.compare), [removedTokens])
  const baseFiltered = useMemo(() => filterTokens(baseTokens, search).sort(Locale.compare), [
    baseTokens,
    search,
  ])
  const invalidList = useMemo(() => [...invalidDomains].sort(Locale.compare), [invalidDomains])

  const editedLength = addedFiltered.length + removedFiltered.length
  const { page: basePaginated, toPage } = usePagination(
    baseFiltered,
    Math.max(MAX_PAGE_SIZE - editedLength, MIN_BASE_ITEMS),
    1
  )

  useEffect(() => {
    setBaseTokens([...new Set(tokens?.split('\n')?.filter(Boolean))])
  }, [tokens])

  useEffect(() => {
    tokensRef.current = {
      addedTokens,
      removedTokens,
      baseTokens,
      validate: () => {
        if (!isDomainsMode) return true

        const allTokens = [...baseTokens, ...addedTokens]
        const invalid = new Set(allTokens.filter((token) => !isValidDomain(token)))
        setInvalidDomains(invalid)

        if (invalid.size > 0) {
          toast.error('invalid domains')
          return false
        }

        return true
      },
    }
  }, [addedTokens, removedTokens, baseTokens, isDomainsMode, tokensRef])

  useEffect(() => {
    if (!isDomainsMode && invalidDomains.size) {
      setInvalidDomains(new Set())
    }
  }, [isDomainsMode, invalidDomains])

  // Bring the invalid-tokens panel into view as soon as validation flags any,
  // so they can be found and fixed regardless of pagination.
  useEffect(() => {
    if (invalidDomains.size && invalidRef.current) {
      invalidRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [invalidDomains])

  const addItems = () => {
    const cleanValues = [...new Set(newTokensInput.split(',').map((value) => value.trim()))]
    let uniqueTokens = cleanValues.filter(
      (value) =>
        !!value &&
        ![...baseTokens, ...addedTokens, ...removedTokens].find((token) => token === value)
    )

    if (isDomainsMode) {
      const invalid = uniqueTokens.filter((token) => !isValidDomain(token))
      if (invalid.length > 0) {
        toast.error('invalid domains')
      }
      uniqueTokens = uniqueTokens.filter((token) => isValidDomain(token))
    }

    if (uniqueTokens.length) {
      setAddedTokens((tokens) => [...tokens, ...uniqueTokens])
      setHasChanges(true)
    }

    setNewTokensInput('')
  }

  const handleToken = ({ value, type }) => {
    // Delete
    if (type === TYPE.BASE) {
      setBaseTokens(removeFromList(value))
      setRemovedTokens(addToList(value))
    }
    // Revert deletion
    if (type === TYPE.REMOVED) {
      setRemovedTokens(removeFromList(value))
      setBaseTokens(addToList(value))
    }
    // Cancel new token
    if (type === TYPE.ADDED) {
      setAddedTokens(removeFromList(value))
    }
    setHasChanges(true)
  }

  // An invalid token lives in either the added or base list; resolve which so the
  // panel's action removes it from the right place.
  const invalidTokenType = (token) => (addedTokens.includes(token) ? TYPE.ADDED : TYPE.BASE)

  const handleInvalidToken = ({ value, type }) => {
    handleToken({ value, type })
    setInvalidDomains((prev) => {
      const next = new Set(prev)
      next.delete(value)
      return next
    })
  }

  const handleRemoveAllInvalid = () => {
    // Base tokens move to the removed list (mirrors deleting one); added tokens
    // are simply dropped (mirrors cancelling a new token).
    const invalidBase = baseTokens.filter((token) => invalidDomains.has(token))
    setBaseTokens((tokens) => tokens.filter((token) => !invalidDomains.has(token)))
    setAddedTokens((tokens) => tokens.filter((token) => !invalidDomains.has(token)))
    setRemovedTokens((prev) => [...prev, ...invalidBase])
    setInvalidDomains(new Set())
    setHasChanges(true)
  }

  const handleKeyPress = (e) => e.key === 'Enter' && addItems()

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text/plain')
    const pastedTokens = pasted.replaceAll(/\r?\n/g, ', ')
    setNewTokensInput((previousTokens) => previousTokens + pastedTokens)
  }

  const handleUploadCSV = (csv = '') => {
    const lines = csv.trim().split(/\r?\n/)
    const hasHeader = lines[0].toLowerCase().includes('tokens')

    let keywords
    if (hasHeader) {
      // eslint-disable-next-line no-unused-vars
      const [_header, ...values] = lines
      keywords = values.map((item) => item.split(',')[0])
    } else {
      keywords = lines.map((item) => item.split(',')[0])
    }

    keywords = keywords.filter((keyword) => keyword !== '')
    keywords = keywords.map((keyword) => keyword.replaceAll(' ', '_').toLocaleLowerCase())

    const uniqueTokens = keywords.filter(
      (value) =>
        !!value &&
        ![...baseTokens, ...addedTokens, ...removedTokens].find((token) => token === value)
    )

    if (isDomainsMode) {
      const invalid = uniqueTokens.filter((token) => !isValidDomain(token))
      if (invalid.length > 0) {
        // Keep invalid tokens in the list and surface them in the panel above so
        // they can be located and fixed, rather than silently dropping them.
        toast.error(`${invalid.length} invalid ${invalid.length === 1 ? 'domain' : 'domains'}`)
        setInvalidDomains((prev) => new Set([...prev, ...invalid]))
      }
    }

    if (uniqueTokens.length) {
      setAddedTokens((tokens) => [...tokens, ...uniqueTokens])
      setHasChanges(true)
    }

    uploadCSVModal.handleCloseModal()
  }

  const handleDownloadCSV = () => {
    const allTokens = [...baseTokens, ...addedTokens]
    const csv = Papa.unparse({
      fields: ['token'],
      data: allTokens.map((token) => [token]),
    })
    downloadCSV(csv, 'content-rule-tokens')
  }

  const handleRemoveAll = () => {
    setRemovedTokens((prev) => [...prev, ...baseTokens])
    setBaseTokens([])
    setAddedTokens([])
    setHasChanges(true)
  }

  const itemsLenght = addedFiltered.length + removedFiltered.length + basePaginated.content.length
  const rowsCount = Math.ceil(itemsLenght / COLUMNS_COUNT)
  const blankItemsToFillSpace = new Array(rowsCount * COLUMNS_COUNT - itemsLenght)
    .fill('')
    .map((_, i) => i)

  return (
    <div className="cr-tokens">
      <If condition={isDomainsMode && invalidList.length > 0}>
        <div className="cr-tokens__invalid" ref={invalidRef}>
          <div className="cr-tokens__invalid-header">
            <Icon
              width="18px"
              height="18px"
              icon="alert-triangle"
              type="outline"
              color="currentColor"
            />
            <span>
              {invalidList.length} invalid {invalidList.length === 1 ? 'domain' : 'domains'} — fix
              or remove {invalidList.length === 1 ? 'it' : 'them'} before saving
            </span>
            <Button
              className="cr-tokens__invalid-remove-all"
              size={BUTTON_SIZE.SMALL}
              theme={BUTTON_THEME.GHOST}
              onClick={handleRemoveAllInvalid}
            >
              <Icon width="18px" height="18px" icon="trash-2" type="outline" color="currentColor" />
              Remove All Invalid
            </Button>
          </div>
          <ul className="cr-tokens__invalid-list">
            <For each="token" of={invalidList}>
              <Token
                key={token}
                value={token}
                type={invalidTokenType(token)}
                onClick={handleInvalidToken}
                isInvalid
              />
            </For>
          </ul>
        </div>
      </If>
      <div className="cr-tokens__header">
        <Input
          label="Tokens List"
          type="text"
          value={newTokensInput}
          placeholder="Add a Token"
          onChange={(value) => setNewTokensInput(value)}
          onKeyPress={handleKeyPress}
          className="cr-tokens__add-tokens"
          description={`${addedTokens.length + removedTokens.length + baseTokens.length} tokens`}
          onPaste={handlePaste}
        />
        <div
          className={classNames('cr-tokens__button-wrapper', {
            'cr-tokens__button-wrapper--active': newTokensInput.length,
          })}
        >
          <IconButton onClick={addItems} iconProps={{ icon: 'add', type: 'fill' }} />
          <div>
            <Button
              className="selected-topics__button"
              size={BUTTON_SIZE.SMALL}
              theme={BUTTON_THEME.GHOST}
              onClick={handleDownloadCSV}
            >
              <Icon
                width="18px"
                height="18px"
                icon="download"
                type="outline"
                color="currentColor"
              />
              CSV Download
            </Button>
            <Button
              className="selected-topics__button"
              size={BUTTON_SIZE.SMALL}
              theme={BUTTON_THEME.GHOST}
              onClick={uploadCSVModal.handleOpenModal}
            >
              <Icon width="18px" height="18px" icon="share-2" type="outline" color="currentColor" />
              CSV Upload
            </Button>
            <Button
              className="selected-topics__button"
              size={BUTTON_SIZE.SMALL}
              theme={BUTTON_THEME.GHOST}
              onClick={handleRemoveAll}
            >
              <Icon width="18px" height="18px" icon="trash-2" type="outline" color="currentColor" />
              Remove All
            </Button>
          </div>
        </div>
        <Input
          type="text"
          placeholder="Search Tokens"
          className="cr-tokens__search"
          value={search}
          onChange={(value) => setSearch(value)}
          theme={INPUT_THEME.SEARCH}
        />
      </div>
      <ul className="cr-tokens__list" style={{ '--rows': rowsCount, '--columns': COLUMNS_COUNT }}>
        <For each="token" of={addedFiltered}>
          <Token
            key={token}
            value={token}
            type={TYPE.ADDED}
            onClick={handleToken}
            isInvalid={isDomainsMode && invalidDomains.has(token)}
          />
        </For>
        <For each="token" of={removedFiltered}>
          <Token key={token} value={token} type={TYPE.REMOVED} onClick={handleToken} />
        </For>
        <For each="token" of={basePaginated.content}>
          <Token
            key={token}
            value={token}
            type={TYPE.BASE}
            onClick={handleToken}
            isInvalid={isDomainsMode && invalidDomains.has(token)}
          />
        </For>
        <For each="token" of={blankItemsToFillSpace}>
          <Token key={token} value={null} type={TYPE.BASE} />
        </For>
      </ul>
      <Pagination
        options={{ length: basePaginated.quantity }}
        page={basePaginated.number - 1}
        onPageChange={(page) => toPage(page.label)}
      />
      <UploadCSVModal
        isOpen={uploadCSVModal.isOpen}
        handleClose={uploadCSVModal.handleCloseModal}
        onUpload={handleUploadCSV}
      />
    </div>
  )
}

Tokens.propTypes = {
  tokens: PropTypes.string.isRequired,
  tokensRef: PropTypes.object.isRequired,
  setHasChanges: PropTypes.func.isRequired,
  tokenType: PropTypes.string,
}

export default Tokens
